// eslint-disable-next-line
import React, { useEffect } from 'react';
import { useLocation, NavLink } from 'react-router-dom';
import SVG from 'react-inlinesvg';
import { toAbsoluteUrl, checkIsActive } from '../../../../_helpers';
import { useDispatch, useSelector } from 'react-redux';
import { getPermission } from '../../../../../store/actions/roleManagement/roleActions';
import * as actions from '../../../../../store/actions/index';
import { useIntl } from 'react-intl';

export function AsideMenuList({ layoutProps }) {
  const location = useLocation();
  const intl = useIntl();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getPermission(localStorage.getItem('role')));
    dispatch(actions.getManageOptionsLanguages());
  }, [dispatch]);

  const { currentPermissions } = useSelector((state) => state.role);
  const { generalSettings } = useSelector((state) => state.settings);
  const { taxonomy } = useSelector((state) => state?.taxonomy);
  const { manageOptionsLanguages } = useSelector(
    (state) => state.manageOptions,
  );

  const getMenuItemActive = (url) =>
    checkIsActive(location, url) ? ' menu-item-active menu-item-open ' : '';

  const has = (key) => currentPermissions?.[key] === 1;

  const setLanguageOnSubMenuChange = () => {
    dispatch(
      actions.setManageOptionsCurrentId(
        manageOptionsLanguages?.find((lng) => lng.isDefault === true).id,
      ),
    );
  };

  const menuConfig = [
    {
      to: '/dashboard',
      icon: '/media/svg/icons/Design/Layers.svg',
      label: 'Dashboard',
      permission: 'list_dashboard',
    },
    {
      to: '/user-management',
      icon: '/media/svg/icons/Communication/Group.svg',
      label: 'User Management',
      children: [
        {
          to: '/user-management/admin',
          label: 'Admin Users',
          permission: 'list_admin_user',
        },
        {
          to: '/user-management/front-end-users',
          label: 'Front-End Users',
          permission: 'list_front_user',
        },
        {
          to: '/user-management/role-management',
          label: 'Role Management',
          permission: 'list_role_management',
        },
        {
          to: '/user-management/accreditation',
          label: 'Accreditation Users',
          permission: 'list_accreditation',
        },
        {
          to: '/user-management/account-verification-users',
          label: 'Account Verification Users',
          permission: 'list_account_verification',
        },
      ],
    },
    {
      to: '/campaign',
      icon: '/media/svg/icons/Design/Layers.svg',
      label: `${taxonomy?.projectSingular} Management`,
      headerLabel: taxonomy?.projectSingular,
      children: [
        {
          to: '/campaign/campaigns',
          label: taxonomy?.projectPlural,
          permission: 'list_campaigns',
        },
        {
          to: '/campaign/categories',
          label: 'Categories',
          permission: 'list_categories',
          onClick: setLanguageOnSubMenuChange,
        },
      ],
    },
    {
      to: '/api-management',
      icon: '/media/svg/icons/Tools/Tools.svg',
      label: 'API Management',
      children: [
        {
          to: '/api-management/payment-gateway',
          label: 'Payment Gateway',
          permission: 'list_payment_gateways',
        },
        {
          to: '/api-management/kyc-settings',
          label: 'KYC Settings',
          permission: 'list_kyc_settings',
        },
        {
          to: '/api-management/social-signup',
          label: 'Social Setup',
          permission: 'list_social_signup',
        },
        {
          to: '/api-management/recaptcha',
          label: 'reCaptcha',
          permission: 'list_reCaptcha',
        },
        {
          to: '/api-management/google-map',
          label: 'Google Map',
          permission: 'list_google_map',
        },
        {
          to: '/api-management/newsletter',
          label: 'Newsletter Settings',
          permission: 'list_newsletter',
        },
      ],
    },
    {
      to: '/transaction',
      icon: '/media/svg/icons/Shopping/Money.svg',
      label: 'Transactions',
      permission: 'list_transaction',
    },
    {
      to: '/wallet',
      icon: '/media/svg/icons/Shopping/Wallet.svg',
      label: 'Wallet',
      gate: generalSettings?.walletModule === 'yes',
      children: [
        {
          to: '/wallet/wallet-transactions',
          label: 'Wallet Transactions',
        },
        {
          to: '/wallet/topup-requests',
          label: 'Top-up Requests',
        },
        {
          to: '/wallet/withdraw-requests',
          label: 'Withdraw Requests',
        },
      ],
    },
    {
      to: '/content-page',
      icon: '/media/svg/icons/Files/File.svg',
      label: 'Content Pages',
      children: [
        {
          to: '/content-page/pages',
          label: 'Pages',
          permission: 'list_pages',
          onClick: setLanguageOnSubMenuChange,
        },
        {
          to: '/content-page/investor-contracts',
          label: 'Investor Contract',
          permission: 'list_investor_contract',
          onClick: setLanguageOnSubMenuChange,
        },
        {
          to: '/content-page/menu',
          label: 'Menu',
          permission: 'list_menu',
          onClick: setLanguageOnSubMenuChange,
        },
        {
          to: '/content-page/faqs',
          label: 'FAQs',
          permission: 'list_faqs',
          onClick: setLanguageOnSubMenuChange,
        },
        {
          to: '/content-page/front-end-faqs',
          label: 'Front-End FAQs',
          permission: 'list_front_end_faqs',
          onClick: setLanguageOnSubMenuChange,
        },
        {
          to: '/content-page/investor-announcements',
          label: 'Announcements',
          permission: 'list_investor_announcements',
          onClick: setLanguageOnSubMenuChange,
        },
      ],
    },
    {
      to: '/manage-options',
      icon: '/media/svg/icons/Home/Commode1.svg',
      label: 'Manage Options',
      children: [
        {
          to: '/manage-options/deal-type',
          label: 'Deal Types',
          permission: 'list_deal_type',
        },
        {
          to: '/manage-options/company-industry',
          label: 'Company Industry',
          permission: 'list_company_industry',
          onClick: setLanguageOnSubMenuChange,
        },
        {
          to: '/manage-options/team-member-type',
          label: 'Team Member Type',
          permission: 'list_team_member_type',
          onClick: setLanguageOnSubMenuChange,
        },
        {
          to: '/manage-options/investor-type',
          label: 'Investor Type',
          permission: 'list_investor_type',
          onClick: setLanguageOnSubMenuChange,
        },
        {
          to: '/manage-options/campaign-stage',
          label: intl.formatMessage(
            {
              id:
                generalSettings?.projectScriptType === 0
                  ? 'CAMPAIGN.STAGE'
                  : 'PROPERTY.STAGE',
            },
            { value: taxonomy?.projectSingular },
          ),
          permission: 'list_campaign_stage',
          onClick: setLanguageOnSubMenuChange,
        },
        {
          to: '/manage-options/investing-round',
          label: 'Investing Round',
          permission: 'list_investing_round',
          onClick: setLanguageOnSubMenuChange,
        },
      ],
    },
    {
      to: '/miscellaneous',
      icon: '/media/svg/icons/Files/Group-folders.svg',
      label: 'Miscellaneous',
      children: [
        {
          to: '/miscellaneous/countries',
          label: 'Countries',
          permission: 'list_countries',
        },
        {
          to: '/miscellaneous/currency',
          label: 'Currency',
          permission: 'list_currency',
        },
        {
          to: '/miscellaneous/cron-jobs',
          label: 'Cron Jobs',
          permission: 'list_cron_jobs',
        },
        {
          to: '/miscellaneous/language',
          label: 'Language',
          permission: 'list_language',
        },
        {
          to: '/miscellaneous/sliders',
          label: 'Sliders',
          permission: 'list_sliders',
          onClick: setLanguageOnSubMenuChange,
        },
      ],
    },
    {
      to: '/secondary-market',
      icon: '/media/svg/icons/Shopping/Cart1.svg',
      label: 'Secondary Market',
      gate:
        generalSettings?.enableSecondaryMarket === 'YES' &&
        has('list_secondary_market'),
      children: [
        { to: '/secondary-market/seller', label: 'Seller' },
        { to: '/secondary-market/buyer', label: 'Buyer' },
        { to: '/secondary-market/settings', label: 'Settings' },
      ],
    },
    {
      to: '/settings',
      icon: '/media/svg/icons/General/Settings-2.svg',
      label: 'Settings',
      children: [
        {
          to: '/settings/general',
          label: 'General',
          permission: 'list_general_setting',
        },
        {
          to: '/settings/settings-campaign',
          label: `${taxonomy?.projectSingular} Settings`,
          permission: 'list_campaign_setting',
        },
        {
          to: '/settings/revenue',
          label: 'Revenue Fees',
          permission: 'list_revenue_setting',
        },
        {
          to: '/settings/date-time',
          label: 'Date and Time',
          permission: 'list_datetime_setting',
        },
        {
          to: '/settings/meta',
          label: 'Meta',
          permission: 'list_meta_setting',
          onClick: setLanguageOnSubMenuChange,
        },
        {
          to: '/settings/amount',
          label: 'Amount',
          permission: 'list_amount_setting',
        },
        {
          to: '/settings/email-templates',
          label: 'Email',
          permission: 'list_email_setting',
        },
      ],
    },
    {
      to: '/spam-settings',
      icon: '/media/svg/icons/General/Settings-1.svg',
      label: 'Spam Settings',
      permission: 'list_spam_setting',
    },
    {
      to: '/contact-list',
      icon: '/media/svg/icons/Communication/Group.svg',
      label: 'Contact Enquiry',
      permission: 'list_contact_enquiry',
    },
    {
      to: '/newsletter',
      icon: '/media/svg/icons/Media/Airplay.svg',
      label: 'Newsletters',
      permission: 'list_newsletters',
    },
    {
      to: '/database-management',
      icon: '/media/svg/icons/General/database.svg',
      label: 'Database Management',
      permission: 'list_database_management',
    },
    {
      to: '/maintenance-mode',
      icon: '/media/svg/icons/General/Settings-2.svg',
      label: 'Maintenance Mode',
      permission: 'maintenance_mode',
    },
  ];

  const isVisible = (node) => {
    if ('gate' in node && !node.gate) return false;
    if (node.permission && !has(node.permission)) return false;
    if (node.children) return node.children.some(isVisible);
    return true;
  };

  const renderChild = (child) => {
    if (!isVisible(child)) return null;
    return (
      <li
        key={child.to}
        role="menuitem"
        className={`menu-item ${getMenuItemActive(child.to)}`}
        aria-haspopup="true"
      >
        <NavLink className="menu-link" to={child.to} onClick={child.onClick}>
          <i className="menu-bullet menu-bullet-dot">
            <span />
          </i>
          <span className="menu-text">{child.label}</span>
        </NavLink>
      </li>
    );
  };

  const renderItem = (item) => {
    if (!isVisible(item)) return null;

    if (item.children) {
      return (
        <li
          key={item.to}
          role="menuitem"
          className={`menu-item menu-item-submenu ${getMenuItemActive(
            item.to,
          )}`}
          aria-haspopup="true"
          data-menu-toggle="hover"
        >
          <NavLink className="menu-link menu-toggle" to={item.to}>
            <span className="svg-icon menu-icon">
              <SVG src={toAbsoluteUrl(item.icon)} />
            </span>
            <span className="menu-text">{item.label}</span>
            <i className="menu-arrow" />
          </NavLink>
          <div className="menu-submenu ">
            <ul className="menu-subnav">
              <li
                role="menuitem"
                className="menu-item  menu-item-parent"
                aria-haspopup="true"
              >
                <span className="menu-link">
                  <span className="menu-text">
                    {item.headerLabel || item.label}
                  </span>
                </span>
              </li>
              {item.children.map(renderChild)}
            </ul>
          </div>
        </li>
      );
    }

    return (
      <li
        key={item.to}
        role="menuitem"
        className={`menu-item ${getMenuItemActive(item.to)}`}
        aria-haspopup="true"
      >
        <NavLink className="menu-link" to={item.to} onClick={item.onClick}>
          <span className="svg-icon menu-icon">
            <SVG src={toAbsoluteUrl(item.icon)} />
          </span>
          <span className="menu-text">{item.label}</span>
        </NavLink>
      </li>
    );
  };

  return (
    <>
      {/* begin::Menu Nav */}
      {/* eslint-disable-next-line */}
      <ul role="menubar" className={`menu-nav ${layoutProps.ulClasses}`}>
        {menuConfig.map(renderItem)}
      </ul>
    </>
  );
}
